import { Images } from "../../../../assets/images";

/*
 * What the new JustCue tab shows between the click on Presentation / Evidence Display and JustAct's answer with the
 * JustCue link: a JustAct card on JustCue's chrome gradient (pale cyan to Dark Cyan), the logo, a spinner, a running
 * bar, the case, and after a few seconds a note that large cases take longer. The tab is about:blank of this app, so
 * the logo loads from here. Purely cosmetic: any failure leaves the blank tab, which still goes to JustCue.
 */

const escapeHtml = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );

const logoUrl = () => {
  const logo = Images.companyLogo;
  const src = typeof logo === "string" ? logo : logo && logo.default;
  try {
    return src ? new URL(src, window.location.href).href : "";
  } catch (e) {
    return "";
  }
};

const STYLE = `
  *{box-sizing:border-box}
  html,body{height:100%;margin:0}
  body{display:flex;align-items:center;justify-content:center;padding:16px;
    font-family:'Open Sans',Arial,sans-serif;color:#293462;
    background:#F2F9F9 linear-gradient(to bottom,#F2F9F9 0%,#F2F9F9 10%,#00818A 100%) fixed}
  .card{width:100%;max-width:440px;background:#fff;border-radius:16px;padding:36px 32px 28px;text-align:center;
    box-shadow:0 12px 40px rgba(41,52,98,.18),0 1px 3px rgba(41,52,98,.12)}
  .logo{height:60px;width:auto;display:block;margin:0 auto 24px}
  .brand{font:700 26px Arial,sans-serif;margin:0 0 28px}
  .brand span{color:#00818A}
  .spinner{width:44px;height:44px;margin:0 auto 22px;border-radius:50%;
    border:4px solid #EAEBEF;border-top-color:#00818A;animation:spin .9s linear infinite}
  h1{font:700 20px/1.3 Arial,sans-serif;margin:0 0 8px}
  .msg{font-size:15px;line-height:1.5;color:#545D81;margin:0}
  .case{display:inline-block;margin:16px 0 0;padding:4px 12px;border-radius:999px;background:#F2F9F9;
    color:#00818A;font:700 13px Arial,sans-serif}
  .bar{position:relative;height:4px;margin:24px 0 0;border-radius:4px;background:#EAEBEF;overflow:hidden}
  .bar span{position:absolute;top:0;bottom:0;left:-40%;width:40%;border-radius:4px;background:#00818A;
    animation:slide 1.4s ease-in-out infinite}
  .slow{font-size:13px;line-height:1.5;color:#545D81;margin:16px 0 0;opacity:0;animation:show .6s ease 6s forwards}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes slide{0%{left:-40%}100%{left:100%}}
  @keyframes show{to{opacity:1}}
  @media (prefers-reduced-motion:reduce){
    .spinner{animation:none;border-top-color:#00818A;border-right-color:#00818A}
    .bar span{animation:none;left:0;width:100%;opacity:.35}
    .slow{animation:none;opacity:1}
  }
`;

/**
 * Writes the waiting page into `win` (a tab this app just opened). `presentation`: the operator's launch, which sends
 * the case documents first; else Evidence Display. `caseLabel`: shown under the message (e.g. "Case 84196").
 */
export function showEpeWaiting(win, { presentation, caseLabel } = {}) {
  if (!win) return;
  try {
    const title = presentation
      ? "Preparing your presentation"
      : "Opening Evidence Display";
    const message = presentation
      ? "Sending the case documents and trial bundles to JustCue…"
      : "Connecting you to the hearing…";
    const slow = presentation
      ? "Large cases can take a minute. Keep this tab open: JustCue opens here by itself."
      : "This can take a few seconds. Keep this tab open: JustCue opens here by itself.";
    const logo = logoUrl();
    const doc = win.document;
    doc.open();
    doc.write(`<!doctype html><html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1"><title>JustCue · ${escapeHtml(
      title,
    )}</title><style>${STYLE}</style></head><body>
<main class="card" role="status" aria-live="polite" aria-busy="true">
${
  logo
    ? `<img class="logo" src="${escapeHtml(logo)}" alt="JustAct">`
    : '<p class="brand">Just<span>Act</span></p>'
}
<div class="spinner" aria-hidden="true"></div>
<h1>${escapeHtml(title)}</h1>
<p class="msg">${escapeHtml(message)}</p>
${caseLabel ? `<p class="case">${escapeHtml(caseLabel)}</p>` : ""}
<div class="bar" aria-hidden="true"><span></span></div>
<p class="slow">${escapeHtml(slow)}</p>
</main></body></html>`);
    doc.close();
  } catch (e) {
    // Cosmetic only: the tab still goes to JustCue.
  }
}
